import { Machine } from "xstate";
import createBackTransitions from "./helpers/createBackTransitions";
import memorizeStep from "./helpers/memorizeStep";

const steps = [
  "welcome",
  "quarter",
  "situation",
  "careerBreak",
  "mutualiteit",
  "unemployed",
  "performedHours",
  "leave",
  "secondaryActivityTrue",
  "secondaryActivityFalse",
  "secondaryActivityUndecided",
];

const stepperMachine = Machine({
  id: "stepper",
  initial: "welcome",
  // initial: "secondaryActivityUndecided",
  context: {
    stack: [],
    memory: {},
  },
  states: {
    welcome: {
      on: {
        NEXT: { target: "quarter", actions: memorizeStep },
      },
    },
    quarter: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: { target: "situation", actions: memorizeStep },
      },
    },
    situation: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.fulltime === "true";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.educationDeferredRenumeration === "false";
            },
          },
          {
            target: "careerBreak",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              const data = event.data;
              return [
                "careerBreak",
                "thematicLeave",
                "flemishCareCredit",
                "timeCreditCare",
                "timeCreditTraining",
              ].includes(data.type);
            },
          },
          {
            target: "performedHours",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              const data = event.data;
              return [
                "education",
                "ordinary",
                "educationWithEmployeeActivity",
                "whiteCollarWorker",
                "blueCollarWorker",
                "teacher",
                "temporaryWorker",
                "weekendTeam",
                "phd",
                "terminationFee",
                "severancePayment",
              ].includes(data.type);
            },
          },
          {
            target: "leave",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.type === "specialLeaveArrangements";
            },
          },
          {
            target: "mutualiteit",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.type === "mutualiteit";
            },
          },
          {
            target: "unemployed",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return (
                event.data.type === "unemployed" ||
                event.data.type === "earlyRetirement"
              );
            },
          },
          {
            target: "secondaryActivityUndecided",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return [
                "flexijob",
                "abroad",
                "specialPensions",
                "governmentPension",
              ].includes(event.data.type);
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return (
                [
                  "individualVocationalTraining",
                  "integration",
                  "livingWage",
                  "retirementPension",
                ].includes(event.data.type) ||
                event.data.situation === "student"
              );
            },
          },
        ],
      },
    },
    careerBreak: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityTrue";
            },
          },
          {
            target: "secondaryActivityUndecided",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityUndecided";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityFalse";
            },
          },
        ],
      },
    },
    leave: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityTrue";
            },
          },
          {
            target: "secondaryActivityUndecided",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityUndecided";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityFalse";
            },
          },
        ],
      },
    },
    performedHours: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityTrue";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityFalse";
            },
          },
        ],
      },
    },
    mutualiteit: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityTrue";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityFalse";
            },
          },
        ],
      },
    },
    unemployed: {
      on: {
        BACK: createBackTransitions(steps),
        NEXT: [
          {
            target: "secondaryActivityTrue",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityTrue";
            },
          },
          {
            target: "secondaryActivityFalse",
            actions: memorizeStep,
            cond: (context: any, event: any) => {
              return event.data.result === "secondaryActivityFalse";
            },
          },
        ],
      },
    },
    secondaryActivityTrue: {
      on: {
        NEXT: { target: "welcome" },
        BACK: createBackTransitions(steps),
      },
    },
    secondaryActivityFalse: {
      on: {
        NEXT: { target: "welcome" },
        BACK: createBackTransitions(steps),
      },
    },
    secondaryActivityUndecided: {
      on: {
        NEXT: { target: "welcome" },
        BACK: createBackTransitions(steps),
      },
    },
  },
});

export default stepperMachine;
